import { useCallback } from "react";
import useProjectAccess from "rwb/hooks/useProjectAccess";
import useWorkbench from "rwb/pages/project/hooks/useProject";
import ProjectPrivilege from "rwb/pages/project/types/enums/privilege";
import Workbench from "rwb/types/interfaces/project";
import yn from "yn";
import useFundingSources from "~/pages/admin/hooks/useFundingSources";
import { apiMutate } from "~/utils/apiClient";

const { VITE_RWB_API, VITE_COST_MANAGEMENT_ENABLED } = import.meta.env;

/**
 * Funding source data and mutation for a project's attached workbench. Reuses
 * the RWB `useProject`/`useProjectAccess` hooks with an explicit workbench id
 * (rather than the `:projectId` route param they normally read) so the
 * nextgen project shell can drive them without any RWB route context.
 */
export const useProjectFundingSource = (workbenchId: number | null) => {
  const enabled = yn(VITE_COST_MANAGEMENT_ENABLED) && workbenchId !== null;
  const { data: workbench, mutate } = useWorkbench(enabled ? workbenchId! : undefined);
  const { data: fundingSources = [] } = useFundingSources(enabled);
  const canEdit = useProjectAccess(ProjectPrivilege.MANAGE_FUNDING_SOURCE, workbench);

  const updateFundingSource = useCallback(
    async (fundingSourceId: string) => {
      const updated = await apiMutate<Workbench>(`${VITE_RWB_API}/projects/${workbenchId}`, "PUT", {
        funding_source: fundingSourceId,
      });
      mutate(updated, { revalidate: false });
    },
    [workbenchId, mutate]
  );

  return {
    enabled,
    fundingSources,
    currentFundingSourceId: workbench?.funding_source,
    canEdit,
    updateFundingSource,
  };
};
